<template>
  <AuthShell
    :title="$t('resetPassword.heroTitle')"
    :subtitle="$t('resetPassword.heroSubtitle')"
  >
    <div class="space-y-6">
      <div>
        <p class="text-sm uppercase tracking-[0.3em] text-steel-400">{{ $t('resetPassword.eyebrow') }}</p>
        <h2 class="mt-3 font-display text-2xl font-semibold text-white">{{ $t('resetPassword.title') }}</h2>
      </div>

      <p v-if="isChecking" class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-steel-200">
        {{ $t('resetPassword.verifying') }}
      </p>

      <form v-else class="space-y-5" @submit.prevent="resetPassword">
        <label class="block text-sm text-steel-200">
          {{ $t('resetPassword.newPassword') }}
          <input
            v-model="password"
            type="password"
            placeholder="New password"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            autocomplete="new-password"
          />
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('resetPassword.confirmPassword') }}
          <input
            v-model="confirmPassword"
            type="password"
            placeholder="Repeat new password"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            autocomplete="new-password"
          />
        </label>

        <button
          type="submit"
          class="w-full rounded-2xl bg-white px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="isLoading || !canReset"
        >
          {{ isLoading ? $t('resetPassword.submitting') : $t('resetPassword.submit') }}
        </button>
      </form>

      <p v-if="statusMessage" class="rounded-2xl border border-neon-400/30 bg-neon-400/10 px-4 py-3 text-sm text-neon-100">
        {{ statusMessage }}
      </p>

      <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ errorMessage }}
      </p>

      <p class="text-center text-xs text-steel-400">
        {{ $t('resetPassword.needFreshLink') }}
        <router-link class="text-neon-400 hover:text-neon-500" to="/forgot-password">{{ $t('resetPassword.requestAnother') }}</router-link>
      </p>
    </div>
  </AuthShell>
</template>

<script>
import AuthShell from '@/components/auth/AuthShell.vue'
import { supabase } from '@/lib/supabase'

export default {
  name: 'ResetPasswordView',
  components: {
    AuthShell,
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      isLoading: false,
      isChecking: true,
      canReset: false,
      statusMessage: '',
      errorMessage: '',
      authSubscription: null,
    }
  },
  async mounted() {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || session?.user) {
        this.canReset = true
        this.isChecking = false
      }
    })
    this.authSubscription = data?.subscription || null

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      this.canReset = Boolean(sessionData?.session?.user || window.location.hash.includes('access_token='))
      if (!this.canReset) {
        this.errorMessage = this.$t('resetPassword.invalidOrExpired')
      }
    } catch (error) {
      this.errorMessage = this.$t('resetPassword.unableToVerify')
    } finally {
      this.isChecking = false
    }
  },
  beforeUnmount() {
    this.authSubscription?.unsubscribe?.()
    this.authSubscription = null
  },
  methods: {
    async resetPassword() {
      if (!this.canReset) {
        this.errorMessage = this.$t('resetPassword.invalidOrExpired')
        return
      }
      if (!this.password || !this.confirmPassword) {
        this.errorMessage = this.$t('resetPassword.enterAndConfirm')
        return
      }
      if (this.password.length < 6) {
        this.errorMessage = this.$t('resetPassword.minLength')
        return
      }
      if (this.password !== this.confirmPassword) {
        this.errorMessage = this.$t('resetPassword.passwordMismatch')
        return
      }

      this.isLoading = true
      this.statusMessage = ''
      this.errorMessage = ''

      const { error } = await supabase.auth.updateUser({ password: this.password })

      this.isLoading = false

      if (error) {
        this.errorMessage = error.message || 'Unable to update your password.'
        return
      }

      this.statusMessage = this.$t('resetPassword.updated')
      await supabase.auth.signOut()
      this.$router.replace('/login?reset=success')
    },
  },
}
</script>
